// src/components/Profile.js
import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import axios from 'axios';
import API_BASE_URL from '../config';
import './Profile.css';
import PlanInfo from './PlanInfo';

const Profile = ({ setUserProfile, userProfile: parentUserProfile }) => {
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [userId, setUserId] = useState(null);
  const fileInputRef = useRef(null);
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [apiKeyStatus, setApiKeyStatus] = useState({ hasApiKey: false, maskedKey: null });
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyError, setApiKeyError] = useState('');
  const [userPlan, setUserPlan] = useState('trial');
  const [totalTokensUsed, setTotalTokensUsed] = useState(0);
  const [tokenLimit, setTokenLimit] = useState(undefined);
  const [trialPeriodEndDate, setTrialPeriodEndDate] = useState(null);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'plan', or 'billing'
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertVariant, setAlertVariant] = useState('danger');
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);

  // Get user-specific localStorage key
  const getPhotoKey = (userId) => `profilePhoto_${userId}`;

  // Fetch API key status
  const fetchApiKeyStatus = async () => {
    try {
      console.error('API key status check');
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found for API key status check');
        setApiKeyStatus({ hasApiKey: false, maskedKey: null });
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/user/openai-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('API key status response:', response.data);
      setApiKeyStatus(response.data);
      return response.data; 
    } catch (error) {
      console.error('Error fetching API key status:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Set status to not configured on error
      setApiKeyStatus({ hasApiKey: false, maskedKey: null });
    }
  };

  // Save API key
  const saveApiKey = async () => {
    if (!openaiApiKey.trim()) {
      setApiKeyError('Please enter your OpenAI API key');
      return;
    }

    setApiKeyLoading(true);
    setApiKeyError('');

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/user/openai-key`, 
        { openaiApiKey: openaiApiKey.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setOpenaiApiKey('');
      const status = await fetchApiKeyStatus();
      console.log('verify apiKeyStatus');
      console.log(status.hasApiKey);
      if (status.hasApiKey == true) {
        console.log('enable loader');
         const loaderOverlay = document.createElement('div');
          loaderOverlay.style.position = 'fixed';
          loaderOverlay.style.top = '0';
          loaderOverlay.style.left = '0';
          loaderOverlay.style.width = '100%';
          loaderOverlay.style.height = '100%';
          loaderOverlay.style.background = 'rgba(0, 0, 0, 0.6)';
          loaderOverlay.style.display = 'flex';
          loaderOverlay.style.flexDirection = 'column';
          loaderOverlay.style.alignItems = 'center';
          loaderOverlay.style.justifyContent = 'center';
          loaderOverlay.style.color = '#fff';
          loaderOverlay.style.fontSize = '18px';
          loaderOverlay.style.zIndex = '9999';

          loaderOverlay.innerHTML = `
            <div class="spinner" style="
              border: 4px solid #f3f3f3;
              border-top: 4px solid #3498db;
              border-radius: 50%;
              width: 40px;
              height: 40px;
              animation: spin 1s linear infinite;
            "></div>
            <p style="margin-top: 16px;">API key added. Refreshing in 5 seconds...</p>
          `;

          document.body.appendChild(loaderOverlay);

          // Add simple CSS animation for the spinner
          const style = document.createElement('style');
          style.innerHTML = `
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `;
          document.head.appendChild(style);
        setTimeout(() => {
          window.location.reload();
        }, 5000);
      }
    } catch (error) {
      console.error('Error saving API key:', error);
      setApiKeyError(error.response?.data?.message || 'Failed to save API key');
    } finally {
      setApiKeyLoading(false);
    }
  };

  // Remove API key
  const removeApiKey = async () => {
    setApiKeyLoading(true);
    setApiKeyError('');

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api/user/openai-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fetchApiKeyStatus();
    } catch (error) {
      console.error('Error removing API key:', error);
      setApiKeyError(error.response?.data?.message || 'Failed to remove API key');
    } finally {
      setApiKeyLoading(false);
    }
  };

  // Load profile photo from localStorage and backend on component mount
  useEffect(() => {
    // Clear any old generic profile photo to prevent cross-user sharing
    localStorage.removeItem('profilePhoto');
    
    // Check if there's a saved photo for the current user
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const savedPhoto = localStorage.getItem(getPhotoKey(tokenPayload.id));
        if (savedPhoto && setUserProfile) {
          // Update parent component with the saved photo immediately
          setUserProfile(prev => prev ? { ...prev, profilePhoto: savedPhoto } : { profilePhoto: savedPhoto });
        }
      } catch (error) {
        console.error('Error parsing token for photo check:', error);
      }
    }
  }, [setUserProfile]);

  // Use parent profile data if available (loaded automatically after login)
  useEffect(() => {
    if (parentUserProfile && Object.keys(parentUserProfile).length > 0) {
      console.log('Profile: Using pre-loaded profile data from parent:', parentUserProfile);
      setProfile(parentUserProfile);
      setLoading(false);
      
      // Set profile photo if available
      if (parentUserProfile.profilePhoto) {
        setProfilePhoto(parentUserProfile.profilePhoto);
      }
      
      // Extract user ID from token for user-specific storage
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (token) {
        try {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          setUserId(tokenPayload.id);
          
          // Save profile photo to user-specific storage
          if (parentUserProfile.profilePhoto) {
            localStorage.setItem(getPhotoKey(tokenPayload.id), parentUserProfile.profilePhoto);
          }
        } catch (error) {
          console.error('Error parsing token for user ID:', error);
        }
        
        // Fetch plan and token info when parent profile is available
        const fetchPlanInfo = async () => {
          try {
            const meResponse = await axios.get(`${API_BASE_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (meResponse.data) {
              setUserPlan(meResponse.data.plan || 'trial');
              setTotalTokensUsed(meResponse.data.totalTokensUsed || 0);
              setTokenLimit(
                Object.prototype.hasOwnProperty.call(meResponse.data, 'tokenLimit')
                  ? meResponse.data.tokenLimit
                  : undefined
              );
              setTrialPeriodEndDate(meResponse.data.trialPeriodEndDate);
            }
          } catch (error) {
            console.error('Error fetching plan info:', error);
          }
        };
        fetchPlanInfo();
      }
    }
  }, [parentUserProfile]);

  // Set loading to false if we have profile data from parent
  useEffect(() => {
    if (parentUserProfile && Object.keys(parentUserProfile).length > 0) {
      setLoading(false);
    }
  }, [parentUserProfile]);

  // Handle tab selection from navigation state or query parameter
  const location = useLocation();
  useEffect(() => {
    // Check query parameter first (e.g., ?tab=billing)
    const urlParams = new URLSearchParams(location.search);
    const tabFromQuery = urlParams.get('tab');
    if (tabFromQuery) {
      setActiveTab(tabFromQuery);
      if (tabFromQuery === 'billing') {
        fetchBillingHistory();
      }
    } else if (location.state && location.state.tab) {
      setActiveTab(location.state.tab);
      if (location.state.tab === 'billing') {
        fetchBillingHistory();
      }
    }
  }, [location.state, location.search]);

  useEffect(() => {

    const runEffect = async () => {      
      await fetchApiKeyStatus();
    };
    runEffect();
    
    // Skip API call if we already have profile data from parent
    if (parentUserProfile && Object.keys(parentUserProfile).length > 0) {     
      console.log('Profile: Skipping API call - using pre-loaded data');
      return;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError('');
        
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        
        if (!token) {
          setError('No authentication token found. Please login again.');
          setLoading(false);
          return;
        }

        console.log('Fetching profile with token:', token.substring(0, 20) + '...');
        console.log('API URL:', `${API_BASE_URL}/auth/profile`);

        const response = await axios.get(`${API_BASE_URL}/auth/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        // Also fetch plan and token info from /api/auth/me
        const meResponse = await axios.get(`${API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (meResponse.data) {
          setUserPlan(meResponse.data.plan || 'trial');
          setTotalTokensUsed(meResponse.data.totalTokensUsed || 0);
          setTokenLimit(
            Object.prototype.hasOwnProperty.call(meResponse.data, 'tokenLimit')
              ? meResponse.data.tokenLimit
              : undefined
          );
          setTrialPeriodEndDate(meResponse.data.trialPeriodEndDate);
        }
        
        console.log('Profile data received:', response.data);
        console.log('Profile photo from backend:', response.data.profilePhoto);
        setProfile(response.data);
        
        // Update the parent component's user profile state
        if (setUserProfile) {
          // Ensure profilePhoto is included in the data sent to parent
          const profileWithPhoto = {
            ...response.data,
            profilePhoto: response.data.profilePhoto || null
          };
          setUserProfile(profileWithPhoto);
        }
        
        // Extract user ID from token for user-specific storage
        try {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          setUserId(tokenPayload.id);
          
          // Clear any old generic profile photo to prevent cross-user sharing
          localStorage.removeItem('profilePhoto');
          
          // Load profile photo from backend if available
          if (response.data.profilePhoto) {
            setProfilePhoto(response.data.profilePhoto);
            localStorage.setItem(getPhotoKey(tokenPayload.id), response.data.profilePhoto);
          } else {
            // Check if there's a saved photo for this specific user
            const savedPhoto = localStorage.getItem(getPhotoKey(tokenPayload.id));
            if (savedPhoto) {
              setProfilePhoto(savedPhoto);
              // Update parent component with the loaded photo
              if (setUserProfile) {
                const profileWithPhoto = {
                  ...response.data,
                  profilePhoto: savedPhoto
                };
                setUserProfile(profileWithPhoto);
              }
            }
          }
        } catch (error) {
          console.error('Error parsing token:', error);
          // Fallback to backend photo if token parsing fails
          if (response.data.profilePhoto) {
            setProfilePhoto(response.data.profilePhoto);
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        if (error.response?.status === 401) {
          setError('Authentication failed. Please login again.');
        } else if (error.response?.status === 404) {
          setError('Profile not found. Please contact support.');
        } else {
          setError(`Error loading profile: ${error.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    fetchApiKeyStatus();
  }, []);

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const photoData = e.target.result;
        setProfilePhoto(photoData);
        
        // Update local profile state
        const updatedProfile = { ...profile, profilePhoto: photoData };
        setProfile(updatedProfile);
        
        // Update the parent component's user profile state
        if (setUserProfile) {
          setUserProfile(updatedProfile);
          console.log('Updated parent profile with photo:', updatedProfile);
        }
        
        // Save to localStorage with user-specific key
        if (userId) {
          localStorage.setItem(getPhotoKey(userId), photoData);
        }
        
        // Save to backend for permanent storage
        try {
          const token = localStorage.getItem('token') || sessionStorage.getItem('token');
          await axios.post(`${API_BASE_URL}/auth/profile/photo`, 
            { profilePhoto: photoData },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('Profile photo saved to backend');
        } catch (error) {
          console.error('Error saving profile photo to backend:', error);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const getInitials = () => {
    const firstName = profile.firstName || '';
    const lastName = profile.lastName || '';
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  };

  const fetchBillingHistory = async () => {
    try {
      setBillingLoading(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/billing/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching billing history:', error);
      setBillingHistory([]);
    } finally {
      setBillingLoading(false);
    }
  };

  const downloadInvoice = async (billingId) => {
    try {
      // Set loading state for this specific invoice
      setDownloadingInvoiceId(billingId);
      
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/billing/invoice/${billingId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Generate invoice number in format "IN" + 6-digit number (e.g., IN102025)
      const invoiceNumber = `IN${billingId.toString().padStart(6, '0')}`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice ${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Clear loading state
      setDownloadingInvoiceId(null);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      setDownloadingInvoiceId(null);
      setAlertMessage('Failed to download invoice. Please try again.');
      setAlertVariant('danger');
      setTimeout(() => setAlertMessage(null), 5000);
    }
  };

  const clearProfilePhoto = async () => {
    setProfilePhoto(null);
    
    // Update local profile state
    const updatedProfile = { ...profile, profilePhoto: null };
    setProfile(updatedProfile);
    
    // Update the parent component's user profile state
    if (setUserProfile) {
      setUserProfile(updatedProfile);
    }
    
    // Remove from localStorage with user-specific key
    if (userId) {
      localStorage.removeItem(getPhotoKey(userId));
    }
    
    // Remove from backend as well
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/auth/profile/photo`, 
        { profilePhoto: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Profile photo removed from backend');
    } catch (error) {
      console.error('Error removing profile photo from backend:', error);
    }
  };

  return (
    <div className="profile-page-bg">
      <div className="profile-container">
        <div className="profile-header">
          <h2>Profile</h2>
          
          {/* Profile Photo Section */}
          {profilePhoto ? (
            <img 
              src={profilePhoto} 
              alt="Profile" 
              className="profile-photo"
            />
          ) : (
            <div className="profile-photo-placeholder">
              {getInitials()}
            </div>
          )}
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handlePhotoUpload}
            accept="image/*"
            className="hidden"
          />
          <div className="profile-header-actions">
            <button 
              className="upload-photo-btn"
              onClick={() => fileInputRef.current.click()}
            >
              {profilePhoto ? 'Change Photo' : 'Upload Photo'}
            </button>
            {profilePhoto && (
              <button 
                className="upload-photo-btn remove-photo-btn"
                onClick={clearProfilePhoto}
              >
                Remove Photo
              </button>
            )}
          </div>
        </div>
        
        {loading && (
          <div className="loading">
            <p>Loading profile data...</p>
          </div>
        )}
        
        {error && (
          <div className="error">
            <p>{error}</p>
            <button onClick={() => window.location.href = '/login'}>
              Go to Login
            </button>
          </div>
        )}
        
        {!loading && !error && (
          <>
            {alertMessage && (
              <Alert variant={alertVariant} dismissible onClose={() => setAlertMessage(null)} className="mb-3">
                {alertMessage}
              </Alert>
            )}
            {/* Tabs */}
            <div className="profile-tabs">
              <button
                className={`profile-tab ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('profile')}
              >
                Profile
              </button>
              <button
                className={`profile-tab ${activeTab === 'plan' ? 'active' : ''}`}
                onClick={() => setActiveTab('plan')}
              >
                Plan
              </button>
              <button
                className={`profile-tab ${activeTab === 'billing' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('billing');
                  fetchBillingHistory();
                }}
              >
                Billing
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'profile' && (
              <div className="profile-data">
                <div className="profile-row">
              <div className="profile-label">First Name:</div>
              <div className="profile-value">{profile.firstName || 'Not set'}</div>
            </div>
            <div className="profile-row">
              <div className="profile-label">Last Name:</div>
              <div className="profile-value">{profile.lastName || 'Not set'}</div>
            </div>
            <div className="profile-row">
              <div className="profile-label">Email:</div>
              <div className="profile-value">{profile.email || 'Not set'}</div>
            </div>
            <div className="profile-row">
              <div className="profile-label">Phone Number:</div>
              <div className="profile-value">{profile.phoneNumber || 'Not set'}</div>
            </div>
            <div className="profile-row">
              <div className="profile-label">Username:</div>
              <div className="profile-value">{profile.username || 'Not set'}</div>
            </div>
            <div className="profile-row">
              <div className="profile-label">Role:</div>
              <div className="profile-value">{profile.role || 'Not set'}</div>
            </div>
            <div className="profile-row">
              <div className="profile-label">Department:</div>
              <div className="profile-value">{profile.department || 'Not set'}</div>
            </div>
            
            {/* OpenAI API Key Section - Hidden but functionality preserved */}
            <div className="profile-section" style={{ display: 'none' }}>
              <h3>OpenAI API Key</h3>
              <div className="api-key-section">
                {apiKeyStatus.hasApiKey ? (
                  <div className="api-key-status">
                    <div className="profile-row">
                      <div className="profile-label">Status:</div>
                      <div className="profile-value" style={{ color: '#28a745' }}>✓ Configured</div>
                    </div>
                    <div className="profile-row">
                      <div className="profile-label">Key:</div>
                      <div className="profile-value">{apiKeyStatus.maskedKey}</div>
                    </div>
                    <button 
                      className="upload-photo-btn"
                      style={{ background: '#dc3545', marginTop: '10px' }}
                      onClick={removeApiKey}
                      disabled={apiKeyLoading}
                    >
                      {apiKeyLoading ? 'Removing...' : 'Remove API Key'}
                    </button>
                  </div>
                ) : (
                  <div className="api-key-input">
                    <div className="profile-row">
                      <div className="profile-label">Status:</div>
                      <div className="profile-value" style={{ color: '#dc3545' }}>✗ Not Configured</div>
                    </div>
                    <div className="api-key-form">
                      <input
                        type="password"
                        placeholder="Enter your OpenAI API key (sk-...)"
                        value={openaiApiKey}
                        onChange={(e) => setOpenaiApiKey(e.target.value)}
                        className="api-key-input-field"
                      />
                      <button 
                        className="upload-photo-btn"
                        onClick={saveApiKey}
                        disabled={apiKeyLoading || !openaiApiKey.trim()}
                        style={{ marginTop: '10px' }}
                      >
                        {apiKeyLoading ? 'Saving...' : 'Save API Key'}
                      </button>
                    </div>
                  </div>
                )}
                
                {apiKeyError && (
                  <div className="error" style={{ marginTop: '10px', padding: '10px', background: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
                    {apiKeyError}
                  </div>
                )}
                
                <div className="api-key-info" style={{ marginTop: '15px', padding: '10px', background: '#e7f3ff', borderRadius: '4px', fontSize: '14px' }}>
                  <strong>Note:</strong> Your OpenAI API key is required to use AI-powered features like test case generation and chat assistance. 
                  The key is stored securely and only used for your account.
                </div>
              </div>
            </div>
              </div>
            )}

            {activeTab === 'plan' && (
              <div className="profile-data">
                {/* Plan Information Section */}
                <PlanInfo
                  userPlan={userPlan}
                  totalTokensUsed={totalTokensUsed}
                  tokenLimit={tokenLimit}
                  trialPeriodEndDate={trialPeriodEndDate}
                  onPlanSwitched={() => {
                    // Refetch user data after plan switch
                    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                    if (token) {
                      axios.get(`${API_BASE_URL}/api/auth/me`, {
                        headers: { Authorization: `Bearer ${token}` }
                      }).then(meResponse => {
                        if (meResponse.data) {
                          setUserPlan(meResponse.data.plan || 'trial');
                          setTotalTokensUsed(meResponse.data.totalTokensUsed || 0);
                          setTokenLimit(
                            Object.prototype.hasOwnProperty.call(meResponse.data, 'tokenLimit')
                              ? meResponse.data.tokenLimit
                              : undefined
                          );
                          setTrialPeriodEndDate(meResponse.data.trialPeriodEndDate);
                        }
                      });
                    }
                  }}
                />
              </div>
            )}

            {activeTab === 'billing' && (
              <div className="profile-data">
                <h3>Billing History</h3>
                {billingLoading ? (
                  <p>Loading billing history...</p>
                ) : billingHistory.length === 0 ? (
                  <p>No bills.</p>
                ) : (
                  <div className="billing-history">
                    <table className="billing-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Plan</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billingHistory.map((item) => (
                          <tr key={item.id}>
                            <td>
                              {new Date(item.createdAt).toLocaleDateString()}
                            </td>
                            <td>{item.plan}</td>
                            <td>
                              ${parseFloat(item.amount || 0).toFixed(2)} {item.currency?.toUpperCase() || 'USD'}
                            </td>
                            <td>
                              <span className={item.status === 'completed' ? 'billing-status-completed' : 'billing-status-failed'}>
                                {item.status}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="billing-download-btn"
                                onClick={() => downloadInvoice(item.id)}
                                disabled={downloadingInvoiceId === item.id}
                              >
                                {downloadingInvoiceId === item.id ? (
                                  <>
                                    <svg 
                                      width="14" 
                                      height="14" 
                                      viewBox="0 0 24 24" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      strokeWidth="2"
                                      style={{ animation: 'spin 1s linear infinite' }}
                                    >
                                      <path d="M21 12a9 9 0 11-6.219-8.56"/>
                                    </svg>
                                    <span>Generating...</span>
                                  </>
                                ) : (
                                  'Download Invoice'
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;

